/// <amd-module name="Wiki/Editor/Components/WikiEditorHeader" />
import * as React from "react";

import { getId } from "VSS/Core/Util/Object";
import { VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { CustomHeader, HeaderTitle, TitleSize } from "VSSUI/Header";
import { CustomHeaderCommandBar } from "VSSUI/HeaderCommandBar";
import { IContextualMenuProps, IMenuProps, MenuButton } from "VSSUI/Menu";
import { SplitButton } from "VSSUI/SplitButton";
import { TextField } from "VSSUI/TextField";
import * as Resources from "Wiki/Editor/Resources";
import { SaveOptionType } from "Wiki/ViewCommon/CustomerIntelligenceConstants";

export const enum WikiEditorViewMode {
    EditAndPreview = "edit-and-preview",
    EditOnly = "edit-only",
    PreviewOnly = "preview-only"
}

export interface IWikiEditorHeaderProps {
    isDirty: boolean;
    pageName: string;
    pageNameError?: string;
    viewMode: WikiEditorViewMode;
    onViewModeChanged: (viewMode: WikiEditorViewMode) => void;
    onCloseEditor: () => void;
    onSavePage: (withMessage: boolean, saveOptionType: SaveOptionType) => void;
    onUpdatePageName: (pageName: string) => void;
    isEditDisabled: boolean;
    hideCloseButton?: boolean;
    shouldFocusTitleOnMount?: boolean;
}

const viewModeButtonId = getId("toggle-viewmode-button");
export class WikiEditorHeader extends VssComponent<IWikiEditorHeaderProps> {
    private _titleField = React.createRef<TextField>();

    public render(): JSX.Element {
        const { pageName, pageNameError } = this.props;
        return (
            <>
                <CustomHeader className="bolt-header-with-commandbar">
                    <HeaderTitle titleSize={TitleSize.Large} className="flex-grow">
                        <FormItem message={pageNameError} error={!!pageNameError}>
                            <TextField
                                className="no-padding"
                                inputClassName="title-m we-pagename-input"
                                ariaLabel={Resources.PageName_Label}
                                value={pageName}
                                onChange={this._onUpdatePageName}
                                placeholder={Resources.PageName_PlaceHolder}
                                ref={this._titleField}
                            />
                        </FormItem>
                    </HeaderTitle>
                    <CustomHeaderCommandBar focusGroupProps={{ defaultElementId: viewModeButtonId }}>
                        <MenuButton
                            hideDropdownIcon
                            id={viewModeButtonId}
                            className="bolt-header-command-item-button"
                            iconProps={{ iconName: "Equalizer" }}
                            ariaLabel={Resources.ViewOptions_Label}
                            contextualMenuProps={this._getViewModes()}
                            subtle
                        />
                        {!this.props.hideCloseButton && (
                            <Button
                                className="bolt-header-command-item-button we-close-btn"
                                text={Resources.CloseButton_Label}
                                onClick={this.props.onCloseEditor}
                            />
                        )}
                        <SplitButton
                            className="bolt-header-command-item-button we-savebutton"
                            disabled={this.props.isEditDisabled || !this.props.isDirty}
                            buttonProps={{
                                className: "we-save-btn",
                                text: Resources.SaveButton_Label,
                                onClick: () => this._onSavePage(false)
                            }}
                            menuButtonProps={{
                                ariaLabel: Resources.SaveDropdown_Label,
                                contextualMenuProps: {
                                    menuProps: {
                                        id: "save-content-menuitems",
                                        items: [
                                            {
                                                id: "save-content-with-message",
                                                text: Resources.SaveRevisionMessage_Label,
                                                iconProps: { iconName: "SaveAs", className: "fontSizeML" },
                                                onActivate: () => this._onSavePage(true)
                                            }
                                        ]
                                    }
                                }
                            }}
                        />
                    </CustomHeaderCommandBar>
                </CustomHeader>
            </>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();

        if (this._titleField.current && this.props.shouldFocusTitleOnMount) {
            this._titleField.current.focus();
        }
    }

    private _onUpdatePageName = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, pageName: string): void => {
        this.props.onUpdatePageName(pageName);
    };

    private _onSavePage = (withMessage: boolean): void => {
        this.props.onSavePage(withMessage, withMessage ? SaveOptionType.SaveWithCustomMessage : SaveOptionType.QuickSaveByClick);
    };

    private _getViewModes(): IContextualMenuProps {
        const allViewModes = [WikiEditorViewMode.EditAndPreview, WikiEditorViewMode.EditOnly, WikiEditorViewMode.PreviewOnly];
        const allViewModeLabels = [Resources.EditAndPreview_Label, Resources.EditOnly_Label, Resources.PreviewOnly_Label];

        return {
            menuProps: {
                id: "view-modes",
                items: allViewModes.map((mode, index) => {
                    return {
                        checked: this.props.viewMode === mode,
                        text: allViewModeLabels[index],
                        id: mode,
                        onActivate: () => {
                            this.props.onViewModeChanged(mode);
                        },
                        readonly: true
                    };
                })
            }
        };
    }
}
